<template>
  <div class="overflow-x-auto">
    <b-table
      ref="tableSalidas"
      :fields="visibleFields"
      :items="items"
      :sort-by.sync="pagination.orderBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      :empty-text="$t('label.noData')"
      no-border-collapse
      show-empty
      borderless
      responsive
      @sort-changed="onSortingChange"
    >
      <div class="text-center loader my-2" slot="table-busy">
        <b-spinner class="align-middle" />
      </div>
      <template #head(actions-right)>
        <select-columns
          :fields.sync="fields"
          :pagination="pagination"
          :cod-table="columnsConfig.codTabla"
        />
      </template>
      <template #cell(actions-left)="row">
        <div class="table-button-container d-flex">
          <b-icon
            class="icon-details ml-3 my-auto cursor-pointer"
            icon="chevron-down"
            aria-label="Collapse Toggle"
            :flip-v="row.detailsShowing"
            @click="row.toggleDetails"
          />
        </div>
      </template>

      <template #cell(fecContable)="row">
        <span
          :id="`fecContable-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecContable)"
        >
          {{ row.value }}
        </span>
      </template>

      <template #cell(fecEntrega)="row">
        <span
          :id="`fecEntrega-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecEntrega)"
        >
          {{ row.value }}
        </span>
      </template>

      <template #cell(fecMovimiento)="row">
        <span
          :id="`fecMovimiento-${divisa}-${row.index}`"
          v-b-tooltip.bottom
          :title="filterLocalTime(row.item.fecMovimiento)"
        >
          {{ row.value }}
        </span>
      </template>

      <template #row-details="row">
        <b-card class="detail-row">
          <detalle-movimiento
            :cliente="getCliente(row.item)"
            :movimiento="row.item"
            :salidas="true"
          />
        </b-card>
      </template>
    </b-table>

    <div class="mt-3" v-if="totalElements > pagination.size">
      <pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :total-elements="totalElements"
        @change="onChangeCurrentPage"
      ></pagination>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import VueFilters from "@/vue-filters";
import {
  ICliente,
  IConfColumnaPais,
  IDivisa,
  IEntrada,
  IField,
  ISaldo,
  ISalida,
  ISoConfColumnasUpdateDTO,
} from "@/entities";
import {
  EDirection,
  IColumnaResponse,
  IMovimientoParams,
  IPaged,
  ISalidaData,
  ISalidaParams,
} from "@/services";
import DetalleMovimiento from "../common/DetalleMovimiento.vue";
import Pagination from "@/components/common/Pagination.vue";
import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";

@Component({
  components: { SelectColumns, DetalleMovimiento, Pagination },
  mixins: [VueFilters],
})
export default class SalidasTable extends Vue {
  @Prop({ required: true }) columnsConfig!: IColumnaResponse;
  @Prop({ required: false, default: null }) saldo!: ISaldo | null;
  @Prop({ required: false, default: null }) divisa!: IDivisa;
  @Prop({ required: false, default: null }) params!: IMovimientoParams;
  @Prop({ required: false, default: false }) isBCR!: boolean;
  @Watch("params", { deep: true })
  onChangeParams(): void {
    this.pagination.page = 1;
    this.getSalidas();
  }
  @Prop({ required: false, default: 0 }) total!: number;
  @Prop({ required: false, default: 0 }) totalElements!: number;
  @Prop({ required: false, default: true }) modal!: boolean;
  @Action("fetchSalidas") fetchSalidas!: (
    params: ISalidaParams
  ) => Promise<ISalidaData | undefined>;
  @Action("fetchSalidasBCR") fetchSalidasBCR!: (
    params: ISalidaParams
  ) => Promise<ISalidaData | undefined>;

  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  @Getter("getClientesAll") getClientes!: ICliente[];

  sortDesc = this.columnsConfig?.codTipoOrden !== "ASC";
  pagination: IPaged = {
    direction: this.sortDesc ? EDirection.DESC : EDirection.ASC,
    orderBy: this.columnsConfig?.codOrden || "fecMovimiento",
    page: 1,
    size: 10,
  };

  salidaResponse: ISalidaData | null = null;

  getCliente(movimiento: IEntrada | ISalida): ICliente | undefined {
    return this.getClientes.find(
      (c) => c.id === Number(movimiento.puntoservicio.clientexdel.cliente.id)
    );
  }

  get items(): ISalida[] {
    return this.salidaResponse?.salidas?.content || [];
  }

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columnas: IField[] = [
      {
        key: "actions-left",
        label: "ACCIONES",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first",
        visible: true,
        class: "table-cell",
        aux: true,
        code: "",
      },
    ];
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columnas.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterLocalDate(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          }
          return value;
        },
      });
    });
    columnas.push({
      key: "actions-right",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas pb-0",
      tdClass: "table-cell",
      visible: true,
      aux: true,
      code: "",
    });
    return columnas;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  isVisible(visibleKeys: string[], column: string): boolean {
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }

  mounted(): void {
    this.getSalidas();
  }

  async getSalidas(): Promise<void> {
    let params: IMovimientoParams;
    if (this.saldo) {
      params = {
        pkSaldo: this.saldo.id,
      };
    } else {
      params = { ...this.params };
      params.pkDivisa = this.divisa.id;
    }
    params.page = this.pagination.page! - 1;
    params.size = this.pagination.size;
    params.orderBy = this.pagination.orderBy;
    params.direction = this.pagination.direction;

    if (this.isBCR) {
      this.salidaResponse = (await this.fetchSalidasBCR(params)) || null;
    } else {
      this.salidaResponse = (await this.fetchSalidas(params)) || null;
    }
    this.$emit("update:total", this.salidaResponse?.totalSalida || 0);
    this.$emit("update:totalElements", this.salidaResponse?.salidas.totalElements);
  }

  onChangeCurrentPage(currentPage: number): void {
    this.pagination.page = currentPage;
    this.getSalidas();
  }

  onSortingChange(): void {
    this.$nextTick(async () => {
      this.pagination.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getSalidas();
      await this.saveColumnsConfig();
    });
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.columnsConfig.codColumna || "",
      codTabla: this.columnsConfig.codTabla,
    };
    if (this.pagination) {
      params.codTipoOrden = this.pagination.direction;
      params.numPagElements = this.pagination.size;
      params.codOrden = this.pagination.orderBy;
    }
    await this.saveColumna(params);
  }
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.table-responsive {
  padding-left: 20px;
  margin-left: -20px;
  padding-right: 20px;
  width: 102%;
}
</style>
